import React, { ReactNode } from "react";
import usePermission from "../hooks/usePermission";
import { PermissionLevel } from "../../core/models/PermissionModels";

interface PermissionGuardProps {
  /** The module to check permissions for (use Modules constants). */
  module: string;
  /** The minimum permission level required. Defaults to Read (1). */
  level?: PermissionLevel;
  /** Content to render when permission is insufficient. Defaults to null (render nothing). */
  fallback?: ReactNode;
  /** Content to render when permission is sufficient. */
  children: ReactNode;
}

/**
 * Conditionally renders children based on the user's permission level for a module.
 *
 * Usage:
 * ```tsx
 * <PermissionGuard module={Modules.Machinery} level={PermissionLevel.Write}>
 *   <Button>Create Machine</Button>
 * </PermissionGuard>
 * ```
 */
const PermissionGuard = ({
  module,
  level = PermissionLevel.Read,
  fallback = null,
  children,
}: PermissionGuardProps) => {
  const { level: userLevel } = usePermission(module);

  if (userLevel >= level) {
    return <>{children}</>;
  }

  return <>{fallback}</>;
};

export default PermissionGuard;
